import { useEffect, type ReactNode, type Ref } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useHideBottomNav } from '../../../contexts/bottom-nav-context';
import { DRAWING_SURFACE_ATTRIBUTE } from './useClearSelectionOutside';
import { splitPictureWidth } from './photo-height';

interface ClimbEditorSplitProps {
  /** The picture's width over its height, or nothing while it loads. */
  aspect: number | null | undefined;
  /** What is drawn on — given the whole of the left column, at full height. */
  picture: ReactNode;
  /** What the column says at its top, beside the way back. */
  title: string;
  /**
   * What acts on the drawing — the bar of hold roles, a circuit's list. Kept at
   * the top of the column while the fields scroll under it, and part of the
   * drawing surface: a press here acts on the hold in hand rather than putting
   * it down (see `useClearSelectionOutside`).
   */
  controls?: ReactNode;
  /**
   * The same box, for whoever renders into it from elsewhere — the photo's
   * surface, which keeps its bar's state to itself and portals the bar here.
   */
  controlsRef?: Ref<HTMLDivElement>;
  /** The fields. */
  children: ReactNode;
}

/**
 * The climb editor on a phone held sideways: the picture on the left, the height
 * of the screen, and everything else in a column on the right that scrolls by
 * itself.
 *
 * Stacked, a sideways phone has room for a strip of wall or a couple of fields,
 * never both. Side by side, the wall is as tall as the screen and the name, the
 * grade and the bar of roles are beside it rather than a scroll away.
 *
 * The whole screen, over the app's own chrome rather than inside it: the navbar
 * and the pill would each take a sixth of a sideways phone's height. The column
 * carries the way back instead — not a breadcrumb, just a way out.
 *
 * The picture's column is as wide as the picture at full height, capped (see
 * `splitPictureWidth`). A wider picture fills it and is panned across, which is
 * the editor's opened-out mode (see `useExpandablePhoto`); a narrower one — a
 * portrait photo — leaves the rest to the fields.
 *
 * The save button floats over the page as it always does, which puts it at the
 * foot of this column.
 */
export function ClimbEditorSplit({
  aspect,
  picture,
  title,
  controls,
  controlsRef,
  children,
}: ClimbEditorSplitProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useHideBottomNav();
  useLockPageScroll();

  return (
    <div className="fixed inset-0 z-40 flex bg-background" data-testid="climb-editor-split">
      <div
        className="relative flex h-full shrink-0 flex-col"
        style={{ width: splitPictureWidth(aspect) }}
        data-testid="climb-editor-split-picture"
      >
        {picture}
      </div>

      <div
        className="flex min-w-0 flex-1 flex-col overflow-y-auto overscroll-contain border-l pb-24"
        data-testid="climb-editor-split-fields"
      >
        <div className="flex items-center gap-1 px-1 pt-1">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label={t('common.back')}
            title={t('common.back')}
            className="flex size-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="size-4" aria-hidden />
          </button>
          <h1 className="truncate text-sm font-semibold">{title}</h1>
        </div>

        <div
          ref={controlsRef}
          className="sticky top-0 z-10 flex flex-col items-center gap-3 bg-background px-2 py-2 empty:hidden"
          data-testid="climb-editor-split-controls"
          {...{ [DRAWING_SURFACE_ATTRIBUTE]: '' }}
        >
          {controls}
        </div>

        {children}
      </div>
    </div>
  );
}

/**
 * The page under the split stays where it is: the split is the whole screen, and
 * a drag that runs off the end of its column would otherwise scroll a page nobody
 * can see.
 */
function useLockPageScroll() {
  useEffect(() => {
    const root = document.documentElement;
    const before = root.style.overflow;
    root.style.overflow = 'hidden';
    return () => {
      root.style.overflow = before;
    };
  }, []);
}
